<template>
  <v-container fluid class="px-0 py-2">
    <v-form
      ref="lineItemForm"
      v-model.trim="formValid"
      lazy-validation
      v-on:submit.stop.prevent="receivePendingItem"
    >
      <!-- Summary Metric Cards Bar -->
      <v-row class="mb-4" v-if="!pageLoading && formData.length > 0">
        <v-col cols="12" sm="4">
          <v-card
            flat
            class="custom-grey-border pa-4 rounded-lg d-flex align-center"
          >
            <v-avatar color="cyan lighten-5" size="48" class="mr-3 rounded-lg">
              <v-icon color="cyan darken-2">mdi-package-variant-closed</v-icon>
            </v-avatar>
            <div>
              <div
                class="caption grey--text text--darken-1 font-weight-600 text-uppercase"
              >
                Pending Line Items
              </div>
              <div
                class="headline font-weight-700 cyan--text text--darken-2 mt-1"
              >
                {{ formData.length }}
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card
            flat
            class="custom-grey-border pa-4 rounded-lg d-flex align-center"
          >
            <v-avatar color="blue lighten-5" size="48" class="mr-3 rounded-lg">
              <v-icon color="blue darken-2"
                >mdi-checkbox-marked-circle-outline</v-icon
              >
            </v-avatar>
            <div>
              <div
                class="caption grey--text text--darken-1 font-weight-600 text-uppercase"
              >
                Selected to Receive
              </div>
              <div
                class="headline font-weight-700 blue--text text--darken-2 mt-1"
              >
                {{ selectedLineItemIds.length }}
                <span class="caption grey--text">/ {{ formData.length }}</span>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card
            flat
            class="custom-grey-border pa-4 rounded-lg d-flex align-center"
          >
            <v-avatar color="teal lighten-5" size="48" class="mr-3 rounded-lg">
              <v-icon color="teal darken-2">mdi-counter</v-icon>
            </v-avatar>
            <div>
              <div
                class="caption grey--text text--darken-1 font-weight-600 text-uppercase"
              >
                Total Pending Qty
              </div>
              <div
                class="headline font-weight-700 teal--text text--darken-2 mt-1"
              >
                {{
                  formData.reduce(
                    (acc, item) =>
                      acc + (Number(item.to_be_received_quantity) || 0),
                    0
                  )
                }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Data Table Container -->
      <v-card flat class="custom-grey-border rounded-lg overflow-hidden mb-6">
        <div
          class="d-flex align-center justify-space-between px-4 py-3 bg-white border-bottom"
        >
          <div class="d-flex align-center">
            <v-icon color="cyan darken-2" class="mr-2"
              >mdi-clipboard-text-clock-outline</v-icon
            >
            <span class="subtitle-1 font-weight-700 color-custom-blue"
              >Pending Line Items Sheet</span
            >
            <v-chip
              v-if="formData.length > 0"
              x-small
              color="cyan lighten-5"
              text-color="cyan darken-3"
              class="ml-2 font-weight-700"
            >
              {{ formData.length }}
              {{ formData.length === 1 ? "item" : "items" }}
            </v-chip>
          </div>

          <div
            v-if="selectedLineItemIds.length > 0"
            class="d-flex align-center"
          >
            <v-btn
              small
              text
              color="cyan darken-2"
              class="font-weight-600 text-none mr-2"
              v-on:click="fillMaxPending"
            >
              <v-icon small left>mdi-tray-arrow-down</v-icon>
              Auto-fill Max Qty
            </v-btn>
            <v-chip
              small
              color="cyan lighten-5"
              text-color="cyan darken-3"
              class="font-weight-600"
            >
              {{ selectedLineItemIds.length }} Selected
            </v-chip>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="width-100">
            <thead>
              <tr class="grey lighten-4">
                <th
                  width="65"
                  class="text-center py-3 caption text-uppercase font-weight-700 grey--text text--darken-2"
                >
                  <v-checkbox
                    :input-value="areAllLineItemsSelected"
                    :indeterminate="hasPartiallySelectedLineItems"
                    :disabled="pageLoading || formData.length === 0"
                    color="cyan"
                    hide-details
                    class="d-inline-flex mt-0 pt-0"
                    aria-label="Select all line items to receive"
                    v-on:change="toggleSelectAll"
                  ></v-checkbox>
                </th>
                <th
                  class="py-3 pl-4 caption text-uppercase font-weight-700 grey--text text--darken-2"
                >
                  Product Details
                </th>
                <th
                  width="160"
                  class="py-3 text-center caption text-uppercase font-weight-700 grey--text text--darken-2"
                >
                  To Be Received Qty
                </th>
                <th
                  width="170"
                  class="py-3 text-center caption text-uppercase font-weight-700 grey--text text--darken-2"
                >
                  Receive Qty
                </th>
                <th
                  width="200"
                  class="py-3 pl-3 caption text-uppercase font-weight-700 grey--text text--darken-2"
                >
                  Serial Numbers
                </th>
                <th
                  width="260"
                  class="py-3 pl-3 caption text-uppercase font-weight-700 grey--text text--darken-2"
                >
                  Remark / Notes
                </th>
                <th
                  width="260"
                  class="py-3 pl-3 pr-4 caption text-uppercase font-weight-700 grey--text text--darken-2"
                >
                  Attachments
                </th>
              </tr>
              <!-- <tr>
                <td colspan="7" class="custom-border-bottom"></td>
              </tr> -->
            </thead>
            <tbody v-if="pageLoading">
              <tr v-for="i in 3" :key="'skeleton-' + i">
                <td colspan="7" class="pa-4">
                  <v-skeleton-loader
                    type="table-row"
                    class="custom-skeleton"
                  ></v-skeleton-loader>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="formData.length > 0">
              <tr
                v-for="(item, index) in formData"
                :key="item.id"
                :class="{
                  'cyan lighten-5': isLineItemSelected(item),
                  'custom-border-bottom': index < formData.length - 1,
                }"
              >
                <!-- Select Checkbox -->
                <td class="text-center align-middle py-2 px-2">
                  <v-checkbox
                    v-model="selectedLineItemIds"
                    :value="item.id"
                    color="cyan"
                    hide-details
                    class="d-inline-flex mt-0 pt-0"
                    :disabled="productLoading || pageLoading"
                    :aria-label="
                      'Select ' +
                      (item.product ? item.product.name : 'line item') +
                      ' to receive'
                    "
                  ></v-checkbox>
                </td>

                <!-- Product -->
                <td class="py-2 pl-4 align-middle">
                  <div class="d-flex align-center">
                    <v-avatar
                      color="cyan lighten-5"
                      size="36"
                      class="mr-3 rounded-lg d-none d-sm-flex"
                    >
                      <v-icon color="cyan darken-2" small
                        >mdi-package-variant</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="d-flex align-center flex-wrap mb-1">
                        <v-chip
                          small
                          label
                          class="lip-type text-uppercase font-weight-600"
                          text-color="white"
                          color="cyan darken-1"
                        >
                          {{ getProductTypeLabel(item) }}
                        </v-chip>
                        <v-chip
                          v-if="isProductSerialized(item)"
                          x-small
                          label
                          color="amber lighten-4"
                          text-color="amber darken-4"
                          class="font-weight-600 ml-2"
                        >
                          Serialized
                        </v-chip>
                        <v-chip
                          outlined
                          v-if="item.product && item.product.barcode"
                          small
                          label
                          color="blue darken-4"
                          class="font-weight-600 ml-2"
                        >
                          {{ item.product.barcode }}
                        </v-chip>
                      </div>
                      <p
                        class="m-0 text-capitalize font-weight-600 color-custom-blue custom-nowrap-ellipsis"
                      >
                        {{ item.product ? item.product.name : "-" }}
                      </p>
                    </div>
                  </div>
                </td>

                <!-- To Be Received Qty -->
                <td class="py-2 px-2 align-middle text-center">
                  <!-- <v-text-field v-model.trim.number="item.to_be_received_quantity" dense filled flat color="cyan"
                    :loading="productLoading" :disabled="productLoading || pageLoading" label="Qty." hide-details solo
                    readonly class="font-weight-700 text-center"></v-text-field> -->
                  <v-avatar
                    color="blue"
                    size="30"
                    class="white--text font-weight-600"
                    >{{ item.to_be_received_quantity }}</v-avatar
                  >
                </td>

                <!-- Receive Qty -->
                <td class="py-2 px-2 align-middle text-center">
                  <v-text-field
                    solo
                    flat
                    v-model.trim.number="item.quantity"
                    dense
                    outlined
                    color="cyan"
                    :loading="productLoading"
                    :disabled="
                      productLoading || pageLoading || !isLineItemSelected(item)
                    "
                    v-on:keydown="preventExceedingQuantity($event, item)"
                    v-on:paste="handleQuantityPaste($event, item)"
                    v-on:input="normalizeQuantity(item)"
                    label="Qty."
                    type="number"
                    min="0"
                    :max="item.to_be_received_quantity"
                    hide-details
                    :rules="[
                      (v) =>
                        !isLineItemSelected(item) ||
                        Number(v) > 0 ||
                        'Receive quantity is required',
                      (v) =>
                        !v ||
                        (v >= 0 && v <= item.to_be_received_quantity) ||
                        'Max: ' + item.to_be_received_quantity,
                    ]"
                  ></v-text-field>
                </td>

                <!-- Serial Numbers -->
                <td class="py-2 pl-3 pr-2 align-middle">
                  <template v-if="isProductSerialized(item)">
                    <div>
                      <div class="d-flex align-center">
                        <v-tooltip top content-class="custom-top-tooltip">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              :color="
                                getSerialCount(item) > 0
                                  ? 'teal darken-2'
                                  : 'orange darken-3'
                              "
                              label
                              small
                              text-color="white"
                              :disabled="!isLineItemSelected(item)"
                              v-on:click="
                                update_serial_numbers_dialog(item, index)
                              "
                              class="text-uppercase font-weight-600 cursor-pointer px-3"
                            >
                              <v-icon small left>{{
                                getSerialCount(item) > 0
                                  ? "mdi-check-circle"
                                  : "mdi-alert-circle"
                              }}</v-icon>
                              {{
                                getSerialCount(item) > 0
                                  ? `Serials (${getSerialCount(item)})`
                                  : "Generate Serials"
                              }}
                            </v-chip>
                          </template>
                          <span>
                            {{
                              getSerialCount(item) > 0
                                ? "Manage Serial Numbers"
                                : "Generate Serial Numbers"
                            }}
                          </span>
                        </v-tooltip>
                      </div>

                      <!-- GENERATED SERIALS PREVIEW UI -->
                      <div
                        v-if="getSerialCount(item) > 0 && false"
                        class="d-flex align-center flex-wrap mt-1"
                      >
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              v-bind="attrs"
                              v-on="on"
                              class="d-flex align-center flex-wrap"
                            >
                              <v-chip
                                v-for="(sn, snIdx) in (Array.isArray(
                                  item.serial_numbers
                                )
                                  ? item.serial_numbers
                                  : (item.serial_numbers || '').split(',')
                                ).slice(0, 2)"
                                :key="snIdx"
                                x-small
                                label
                                color="cyan lighten-5"
                                text-color="cyan darken-3"
                                class="font-weight-600 mr-1 mb-1"
                              >
                                {{ typeof sn === "object" ? sn.text : sn }}
                              </v-chip>
                              <v-chip
                                v-if="getSerialCount(item) > 2"
                                x-small
                                label
                                color="grey lighten-3"
                                text-color="grey darken-3"
                                class="font-weight-600 mb-1"
                              >
                                +{{ getSerialCount(item) - 2 }} more
                              </v-chip>
                            </div>
                          </template>
                          <span>
                            {{
                              (Array.isArray(item.serial_numbers)
                                ? item.serial_numbers.map((s) =>
                                    typeof s === "object" ? s.text : s
                                  )
                                : (item.serial_numbers || "").split(",")
                              )
                                .filter(Boolean)
                                .join(", ")
                            }}
                          </span>
                        </v-tooltip>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <v-chip
                      small
                      label
                      color="grey lighten-4"
                      text-color="grey darken-2"
                      class="font-weight-500 px-2"
                    >
                      <v-icon left x-small color="grey"
                        >mdi-cube-outline</v-icon
                      >
                      Standard Item
                    </v-chip>
                  </template>
                </td>

                <!-- Remark -->
                <!-- Remark -->
                <td class="py-2 pl-3 pr-2 align-middle">
                  <v-textarea
                    v-model.trim="item.remark"
                    solo
                    dense
                    flat
                    outlined
                    rows="2"
                    no-resize
                    hide-details
                    color="cyan"
                    placeholder="Add remark or notes..."
                    class="remark-textarea"
                    :maxlength="500"
                    :disabled="
                      productLoading || pageLoading || !isLineItemSelected(item)
                    "
                  ></v-textarea>
                </td>

                <!-- Attachments -->
                <td class="py-2 pl-3 pr-4 align-middle">
                  <v-btn
                    block
                    depressed
                    color="grey lighten-4"
                    class="attachment-field text-none rounded-lg"
                    :disabled="
                      productLoading || pageLoading || !isLineItemSelected(item)
                    "
                    v-on:click="openAttachmentDialog(item)"
                  >
                    <div
                      class="d-flex align-center text-left attachment-field__content width-100"
                    >
                      <v-avatar
                        size="32"
                        :color="
                          getAttachmentCount(item)
                            ? 'cyan lighten-5'
                            : 'grey lighten-3'
                        "
                        class="mr-2 flex-shrink-0"
                      >
                        <v-icon
                          small
                          :color="
                            getAttachmentCount(item)
                              ? 'cyan darken-2'
                              : 'grey darken-1'
                          "
                          >mdi-paperclip</v-icon
                        >
                      </v-avatar>
                      <div class="flex-grow-1 overflow-hidden">
                        <div
                          class="font-weight-600 text-truncate"
                          :class="
                            getAttachmentCount(item)
                              ? 'cyan--text text--darken-2'
                              : 'grey--text text--darken-3'
                          "
                        >
                          {{
                            getAttachmentCount(item)
                              ? "Manage attachments"
                              : "Add attachments"
                          }}
                        </div>
                        <div
                          class="caption grey--text text--darken-1 text-truncate"
                        >
                          {{
                            getAttachmentCount(item)
                              ? getAttachmentCount(item) +
                                (getAttachmentCount(item) === 1
                                  ? " file selected"
                                  : " files selected")
                              : "Click to select files"
                          }}
                        </div>
                      </div>
                      <v-icon
                        small
                        color="grey darken-1"
                        class="ml-1 flex-shrink-0"
                        >mdi-chevron-right</v-icon
                      >
                    </div>
                  </v-btn>
                </td>
              </tr>
            </tbody>

            <!-- Empty State -->
            <tbody v-else>
              <tr>
                <td colspan="7" class="py-12 text-center">
                  <div
                    class="d-flex flex-column align-center justify-center py-6"
                  >
                    <v-avatar color="cyan lighten-5" size="72" class="mb-3">
                      <v-icon size="40" color="cyan darken-2"
                        >mdi-package-variant-closed-check</v-icon
                      >
                    </v-avatar>
                    <h3
                      class="subtitle-1 font-weight-700 color-custom-blue mb-1"
                    >
                      No Pending Line Items
                    </h3>
                    <p class="caption grey--text text--darken-1 mb-0">
                      There are no line items pending for receiving at the
                      moment for this purchase order.
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- Action Footer -->
      <v-card
        v-if="filteredFormData.length > 0"
        flat
        class="custom-grey-border rounded-lg px-4 py-3 d-flex align-center justify-space-between flex-wrap"
      >
        <div class="d-flex align-center">
          <v-icon color="cyan darken-2" class="mr-2"
            >mdi-information-outline</v-icon
          >
          <span class="caption-1 font-weight-600 grey--text text--darken-3">
            <strong>{{ filteredFormData.length }}</strong>
            {{ filteredFormData.length === 1 ? "item" : "items" }} ready to be
            received
          </span>
        </div>
      </v-card>
    </v-form>

    <!-- Dialogs -->
    <POSerialNumberDialog
      :dialogStatus="serial_number_dialog"
      :itemData="serial_number_object"
      v-on:close="close_serial_number_dialog"
      v-on:save="saveData($event)"
    />

    <Dialog :commonDialog="attachmentDialog" :dialogWidth="800">
      <template v-slot:title>
        <div class="d-flex align-center">
          <v-icon color="cyan" class="mr-2">mdi-paperclip</v-icon>
          <span>Product Attachments</span>
        </div>
      </template>
      <template v-slot:body>
        <div
          v-if="attachmentLineItem && attachmentLineItem.product"
          class="pa-3 mb-4 rounded-lg cyan lighten-5 border-cyan-light d-flex align-center"
        >
          <v-icon color="cyan darken-2" class="mr-2"
            >mdi-package-variant</v-icon
          >
          <div class="font-size-15 font-weight-600 color-custom-blue">
            {{ attachmentLineItem.product.name }}
          </div>
        </div>
        <DocumnetList
          v-if="attachmentLineItem"
          :value="attachmentLineItem.receiveFiles"
          :document-type="18"
          v-on:change="updateItemAttachmentFiles(attachmentLineItem, $event)"
        ></DocumnetList>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button px-6"
          v-on:click="closeAttachmentDialog"
        >
          Done
        </v-btn>
      </template>
    </Dialog>
  </v-container>
</template>

<script>
import { GET, PUT } from "@/core/services/store/request.module";
import ValidationMixin from "@/core/plugins/validation-mixin";
import POSerialNumberDialog from "./PO-Serial-Number-Dialog.vue";
import DocumnetList from "@/view/pages/DocumnetList.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import { ErrorEventBus } from "@/core/lib/message.lib";

export default {
  mixins: [ValidationMixin],
  name: "pending-line-item",
  components: {
    POSerialNumberDialog,
    DocumnetList,
    Dialog,
  },
  props: {
    purchaseOrder: {
      required: true,
      type: Number,
      default: 0,
    },
  },
  data: () => {
    return {
      formValid: true,
      productLoading: false,
      pageLoading: true,
      attachmentDialog: false,
      attachmentLineItem: null,
      selectedLineItemIds: [],
      pendingLineItem: new Array(),
      formData: new Array(),
      dialog_key: Number(new Date()),
      serial_number_dialog: false,
      serial_number_object: {},
      serial_number_array: [],
      numberIndex: -1,
    };
  },
  computed: {
    filteredFormData() {
      if (!this.selectedLineItemIds || this.selectedLineItemIds.length === 0) {
        return [];
      }
      return this.formData.filter((item) =>
        this.selectedLineItemIds.some((id) => id == item.id)
      );
    },
    areAllLineItemsSelected() {
      return (
        this.formData.length > 0 &&
        this.formData.every((item) => this.isLineItemSelected(item))
      );
    },
    hasPartiallySelectedLineItems() {
      return (
        this.selectedLineItemIds.length > 0 && !this.areAllLineItemsSelected
      );
    },
  },
  watch: {
    selectedLineItemIds(value) {
      this.$emit("selection:changed", value.length > 0);
    },
  },
  methods: {
    normalizeQuantity(item) {
      if (item.quantity === "" || item.quantity === null) return;

      const maxQuantity = Number(item.to_be_received_quantity) || 0;
      const quantity = Number(item.quantity);

      if (!Number.isFinite(quantity) || quantity < 0) {
        this.$set(item, "quantity", 0);
      } else if (quantity > maxQuantity) {
        this.$set(item, "quantity", maxQuantity);
      }
    },
    preventExceedingQuantity(event, item) {
      if (["-", "+", ".", "e", "E"].includes(event.key)) {
        event.preventDefault();
        return;
      }

      if (!/^\d$/.test(event.key)) return;

      const input = event.target;
      const value = input.value || "";
      const start = input.selectionStart ?? value.length;
      const end = input.selectionEnd ?? value.length;
      const nextValue = value.slice(0, start) + event.key + value.slice(end);
      const maxQuantity = Number(item.to_be_received_quantity) || 0;

      if (Number(nextValue) > maxQuantity) {
        event.preventDefault();
      }
    },
    handleQuantityPaste(event, item) {
      event.preventDefault();

      const pastedValue = event.clipboardData
        ? event.clipboardData.getData("text").trim()
        : "";

      if (!/^\d+$/.test(pastedValue)) return;

      const maxQuantity = Number(item.to_be_received_quantity) || 0;
      this.$set(item, "quantity", Math.min(Number(pastedValue), maxQuantity));
    },
    isLineItemSelected(item) {
      return this.selectedLineItemIds.some((id) => id == item.id);
    },
    toggleSelectAll(selected) {
      this.selectedLineItemIds = selected
        ? this.formData.map((item) => item.id)
        : [];
    },
    isProductSerialized(item) {
      if (!item) return false;
      const product = item.original_product || item.product;
      if (!product) return false;
      if (Object.prototype.hasOwnProperty.call(product, "is_serialized")) {
        return product.is_serialized === true || product.is_serialized === 1;
      }
      return Number(product.isSerialized) === 1;
    },
    getProductTypeLabel(item) {
      const product = item && (item.original_product || item.product);
      const productType = product && product.product_type;

      return productType === "parts" ? "Parts" : "Product";
    },
    isProductOrParts(item) {
      const product = item && (item.original_product || item.product);
      const productType = product && product.product_type;

      return productType === "goods" || productType === "parts";
    },
    update_serial_numbers_dialog(row, index) {
      this.serial_number_object = {
        ...row,
      };
      this.numberIndex = index;
      this.dialog_key = Number(new Date());
      this.serial_number_dialog = true;
    },
    close_serial_number_dialog() {
      this.serial_number_object = {};
      this.numberIndex = -1;
      this.serial_number_dialog = false;
    },
    saveData(payload) {
      if (this.numberIndex >= 0 && this.formData[this.numberIndex]) {
        const item = this.formData[this.numberIndex];
        const serials =
          payload && Array.isArray(payload.serial_numbers)
            ? payload.serial_numbers
            : [];
        item.serial_numbers = serials;
        if (serials.length > 0) {
          item.quantity = serials.length;
        }
        if (payload && payload.serial_prefix !== undefined) {
          this.$set(item, "serial_prefix", payload.serial_prefix);
        }
        if (payload && payload.serial_prefix_no !== undefined) {
          this.$set(item, "serial_prefix_no", payload.serial_prefix_no);
        }
      }
      this.close_serial_number_dialog();
    },
    getSerialCount(item) {
      if (!item || !item.serial_numbers) return 0;
      if (Array.isArray(item.serial_numbers)) return item.serial_numbers.length;
      if (
        typeof item.serial_numbers === "string" &&
        item.serial_numbers.trim()
      ) {
        return item.serial_numbers.split(",").filter(Boolean).length;
      }
      return 0;
    },
    fillMaxPending() {
      for (let i = 0; i < this.filteredFormData.length; i++) {
        this.filteredFormData[i].quantity =
          this.filteredFormData[i].to_be_received_quantity;
      }
    },
    getPendingLineItems() {
      const _this = this;
      _this.pageLoading = true;
      _this.formData = new Array();
      _this.pendingLineItem = new Array();
      _this.$store
        .dispatch(GET, {
          url: "purchase-order/" + _this.purchaseOrder + "/pending-receives",
        })
        .then(({ data }) => {
          _this.pendingLineItem = Array.isArray(data)
            ? data.filter((item) => _this.isProductOrParts(item))
            : [];
          if (_this.lodash.isEmpty(_this.pendingLineItem) == false) {
            for (let i = 0; i < _this.pendingLineItem.length; i++) {
              _this.formData.push({
                id: _this.pendingLineItem[i].id,
                quantity: null,
                serial_numbers: [],
                adjust_serial_array: [],
                remark: "",
                receiveFiles: [],
                receiveAttachments: [],
                product: _this.pendingLineItem[i].product,
                original_product: _this.pendingLineItem[i].original_product,
                to_be_received_quantity: _this.pendingLineItem[i].quantity,
              });
            }
          }
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    receivePendingItem() {
      const _this = this;

      if (!_this.$refs.lineItemForm.validate()) {
        return false;
      }

      let formData = new Array();
      for (let i = 0; i < _this.filteredFormData.length; i++) {
        const item = _this.filteredFormData[i];

        if (_this.isProductSerialized(item)) {
          const serialCount = _this.getSerialCount(item);
          if (
            serialCount > 0 &&
            (!item.quantity || parseInt(item.quantity) <= 0)
          ) {
            item.quantity = serialCount;
          }
          if (
            item.quantity &&
            parseInt(item.quantity) > 0 &&
            parseInt(item.quantity) !== serialCount
          ) {
            const product = item.original_product || item.product || {};
            ErrorEventBus.$emit(
              "update:error",
              `${
                product.barcode || product.name || "Product"
              }, Ensure that the received quantity entered matches the Serial Number count`
            );
            return false;
          }
        }

        if (item.quantity && parseInt(item.quantity) > 0) {
          const qty = parseInt(item.quantity);
          if (qty > item.to_be_received_quantity) {
            _this.logError(
              `Receive Qty for ${
                item.product ? item.product.name : "product"
              } cannot exceed pending quantity (${
                item.to_be_received_quantity
              }).`
            );
            return false;
          }
          formData.push({
            id: item.id,
            quantity: qty,
            serial_numbers: Array.isArray(item.serial_numbers)
              ? item.serial_numbers.join(",")
              : item.serial_numbers || "",
            remark: item.remark || "",
            attachments: item.receiveAttachments || [],
          });
        }
      }

      if (formData.length === 0) {
        _this.logError(
          "Please enter receive quantity for at least one product."
        );
        return false;
      }

      _this.productLoading = true;
      _this.$store
        .dispatch(PUT, {
          url: "purchase-order/" + _this.purchaseOrder + "/receive",
          data: {
            line_item: formData,
          },
        })
        .then(() => {
          _this.selectedLineItemIds = [];
          _this.getPendingLineItems();
          _this.$emit("reload:content");
          _this.$emit("receipt:saved");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.productLoading = false;
        });
    },
    updateItemAttachmentFiles(item, attachments) {
      item.receiveFiles = Array.isArray(attachments) ? attachments : [];
      item.receiveAttachments = item.receiveFiles
        .map((attachment) =>
          attachment && typeof attachment === "object"
            ? attachment.id
            : attachment
        )
        .filter((attachment) => Number(attachment) > 0);
    },
    getAttachmentCount(item) {
      return item && Array.isArray(item.receiveAttachments)
        ? item.receiveAttachments.length
        : 0;
    },
    openAttachmentDialog(item) {
      this.attachmentLineItem = item;
      this.attachmentDialog = true;
    },
    closeAttachmentDialog() {
      this.attachmentDialog = false;
      this.attachmentLineItem = null;
    },
  },
  mounted() {
    this.getPendingLineItems();
  },
};
</script>

<style scoped>
.attachment-field {
  height: 64px !important;
}

.attachment-field__content {
  width: 100%;
  min-width: 0;
}
/* remark textarea css */
.remark-textarea ::v-deep .v-input__control {
  height: 40px !important;
}
.remark-textarea ::v-deep .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
  background-color: #fafbfc !important;
  border-radius: 8px !important;
  align-items: flex-start !important;
  padding: 4px 4px !important;
}

.remark-textarea ::v-deep textarea {
  font-size: 13px !important;
  line-height: 1.4 !important;
  margin-top: 0 !important;
}

.remark-textarea.v-input--is-disabled ::v-deep .v-input__slot {
  background-color: #f1f5f9 !important;
  border-color: #e2e8f0 !important;
}
</style>
